import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    Typography,
    Select,
    MenuItem,
    Switch,
} from "@mui/material";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {useMutation, useQuery} from "@tanstack/react-query";
import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import {useEffect, useState} from "react";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import IconButton from "@mui/material/IconButton";
import {AddCircleOutline, Delete} from "@mui/icons-material";
import {useSnackbar} from "notistack";

interface PageData {
    status_code: number
    status_text: string

    blocked_email_domains: EmailDomains[]
}

interface EmailDomains {
    id: number
    email_domain: string
}

export default function AdminBlockedEmailDomains() {
    const {enqueueSnackbar} = useSnackbar();

    const [pageData, setPageData] = useState<PageData>();
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [filteredRows, setFilteredRows] = useState<EmailDomains[]>([]);
    const [searchTerm, setSearchTerm] = useState("");    
    const [openNewWordsDialog, setOpenNewWordsDialog] = useState(false);

    // Query for fetching spam words data.
    const pageDataQuery = useQuery({
        queryKey: ["blockedEmailDomianPageData"],
        queryFn: () => authenticateAndFetchData("/admin/admin-get-blocked-email-domains/"),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    })

    useEffect(() => {
        if (pageDataQuery.data) {
            const data = pageDataQuery.data.data;
            setPageData(data);
            setFilteredRows(data.blocked_email_domains);
        }
    }, [pageDataQuery.data]);

    useEffect(() => {
        if (pageData?.blocked_email_domains) {
        const filtered = pageData.blocked_email_domains.filter((row) =>
            row.email_domain.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredRows(filtered);
        }
    }, [searchTerm, pageData]);

    // Mutation for deleting one word.
    const deleteBlockedDomainMutation = useMutation({
        mutationKey: ["deleteSingleBlockedDomain"],
        mutationFn: (id: number) => authenticateAndPostData("/admin/admin-add-or-remove-blocked-email-domain/", {
            action: "remove",
            ids: [id],
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            pageDataQuery.refetch().then();
            enqueueSnackbar("Domain removed successfully", {
                variant: "success",
            });
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, {
                variant: "error",
            });
        }
    });

    // Mutation for deleting multiple words.
    const deleteSelectedBlockedEmailDomainMutation = useMutation({
        mutationKey: ["deleteMultipleBlockedDomains"],
        mutationFn: (ids: number[]) => authenticateAndPostData("/admin/admin-add-or-remove-blocked-email-domain/", {
            action: "remove",
            ids: ids,
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            pageDataQuery.refetch().then();
            enqueueSnackbar("Blocked Email Domain removed successfully", {
                variant: "success",
            });
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, {
                variant: "error",
            });
        }
    });

    // Enabled/disables actions.
    useEffect(() => {
        setActionsDisabled(
            (selectedRows.length === 0) || deleteBlockedDomainMutation.isPending || deleteSelectedBlockedEmailDomainMutation.isPending
        )
    }, [selectedRows, deleteBlockedDomainMutation.isPending, deleteSelectedBlockedEmailDomainMutation.isPending]);


    const columns: GridColDef<EmailDomains>[] = [
        {
            field: "email_domain",
            headerName: "Email Domains",
            flex: 1,
            minWidth: 200,
            hideable: false,
            editable: false,
            filterable: true,
            sortable: true,
        },        
        {
            field: "delete",
            headerName: "Delete",
            filterable: false,
            sortable: false,
            hideable: false,
            editable: false,
            renderCell: (params) => {
                return (
                    <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteSingle(params.row.id)}
                    >
                        <Delete/>
                    </IconButton>
                )
            }
        }
    ];

    function handleDeleteSingle(id: number) {
        deleteBlockedDomainMutation.mutate(id);
    }

    function handleDeleteSelected(ids: number[]) {
        deleteSelectedBlockedEmailDomainMutation.mutate(ids);
    }

    // ============================================================
    // --------------------- MAIN RENDER CODE ---------------------
    // ============================================================

    if (pageDataQuery.isLoading) {
        return (
            <PageLoading/>
        )

    } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

    } else if (pageData) {
        return (
            <Stack direction={"column"} spacing={2} sx={{pb: 4}}>
                <Typography fontWeight={"bold"} variant={"h5"}>Manage Blocked Email Domains</Typography>
                <Divider/>
                <Stack direction={"column"} spacing={2}>
                    {/* Table Actions */}
                    <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
                        <Stack direction={"row"} spacing={2}>                           
                            <Button variant={"contained"}
                                            size={"small"}
                                            startIcon={<AddCircleOutline/>}
                                            disabled={false}
                                            onClick={() => setOpenNewWordsDialog(true)}>
                                Add New Blocked Email Domains
                            </Button>
                            <Button color={"error"}
                                            variant={"contained"}
                                            size={"small"}
                                            startIcon={<Delete/>}
                                            disabled={actionsDisabled}
                                            onClick={() => {
                                                handleDeleteSelected(selectedRows);
                                            }}>
                                Delete Selected
                            </Button>
                        </Stack>
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search email domains..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Box>
                    {/* Table */}
                    <Box sx={{width: '100%'}}>
                        <DataGrid
                            rows={filteredRows}
                            columns={columns}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10,
                                    },
                                },
                            }}
                            pageSizeOptions={[10, 50, 100]}
                            checkboxSelection
                            disableRowSelectionOnClick
                            onRowSelectionModelChange={rowSelectionModel => {
                                setSelectedRows(rowSelectionModel as number[])
                            }}
                        />
                    </Box>
                </Stack>

                <AddBlockedEmailDomainDialog open={openNewWordsDialog}
                                                     onClose={() => setOpenNewWordsDialog(false)}
                                                     onSuccess={() => {
                                                         setOpenNewWordsDialog(false);
                                                         pageDataQuery.refetch().then();
                                                     }}/>
            </Stack>
        )
    } else {
        // Ideally it should not reach here.
        return <></>
    }
}

function AddBlockedEmailDomainDialog(props: {
    open: boolean,
    onClose: () => void,
    onSuccess: () => void,
}) {
    const {enqueueSnackbar} = useSnackbar();

    const [blockedEmailText, setBlockedEmailtext] = useState<string>('');

    // Mutation to add spam words.
    const addBlacklistMutation = useMutation({
        mutationKey: ["addBlockedEmailDomains"],
        mutationFn: (blockedEmailDomians: { emailDomain: string }[]) => authenticateAndPostData("/admin/admin-add-or-remove-blocked-email-domain/", {
            action: "add",
            blockedEmailDomians: blockedEmailDomians
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: (response) => {
            setBlockedEmailtext("");
            props.onSuccess();
            let duplicate_domains: string[] = response.data["duplicate_domains"];
            let error: string = response.data["error"];
            if (duplicate_domains) {
                    enqueueSnackbar(error, {
                        variant: "error",
                    });
            }else{
            enqueueSnackbar("Blocked Email Domain added successfully", {
                variant: "success",
            });
        }
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, {
                variant: "error",
            });
        }
    });

    function handleSubmit() {
        let blockedEmailDomians: { emailDomain: string }[] = [];
        
        blockedEmailText.trim().split("\n").forEach(line => {
            const [emailDomain] = line.split("|").map(x => x.trim());
            if (emailDomain) {
                blockedEmailDomians.push({ emailDomain });
            }
        });

        if (blockedEmailDomians.length > 0) {
            addBlacklistMutation.mutate(blockedEmailDomians);
        }
    }

    // ============================================================
    // --------------------- MAIN RENDER CODE ---------------------
    // ============================================================

    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add Blocked Email Domains</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{mb: 2}}>
                    Enter new blocked email domains, one per line eg. example.com:
                </DialogContentText>
                <TextField
                    autoFocus
                    multiline
                    rows={8}
                    fullWidth
                    variant="outlined"
                    placeholder="Enter one per line: domain | name"
                    value={blockedEmailText}
                    onChange={(e) => setBlockedEmailtext(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}
                                color={"primary"}
                                disabled={addBlacklistMutation.isPending}>
                    Cancel
                </Button>
                <Button color={"primary"}
                                variant={"contained"}
                                onClick={handleSubmit}
                                disabled={addBlacklistMutation.isPending || !blockedEmailText.trim()}>
                    Add Blocked Email Domains
                </Button>
            </DialogActions>
        </Dialog>
    );
}
